import { Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import type { FirmOfferDetailDto, SalesOrderLineItemDto } from '../types/salesOrder.types';
import { aggregateRebarPricing, kgToMt } from '../utils/pricing';
import { totalLineWeightKg } from '../utils/lineWeight';
import {
  combinedOrderTotal,
  computeSoFreight,
  resolveIncludeFreightLine,
} from '../utils/freight';

// Freight computation + combined order total for the SO Line Items footer (ticket
// 86d39jbhk) — mirrors quotation's Weight Breakdown placement (the bg-muted/40 band
// below the table). Reads the shared pricing + freight utils, so its figures match the
// Pricing panel's combined total exactly. Renders nothing when there's no delivered weight.

const PHP_FMT = new Intl.NumberFormat('en-PH', {
  style: 'currency',
  currency: 'PHP',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const fmtPhp = (n: number) => PHP_FMT.format(n);
const fmtMt = (kg: number) => kgToMt(kg).toLocaleString('en-PH', { maximumFractionDigits: 3 });

// Discrete primitive props (not the whole draft) so the block skips re-render when an
// unrelated Order Details field changes — typing in e.g. Customer PO # / Discount Details
// no longer re-runs aggregateRebarPricing here (86d3afaup, perf phase B).
interface Props {
  lineItems: SalesOrderLineItemDto[];
  firmOffer: FirmOfferDetailDto | null;
  /** 86d3fteqa: effective freight rate per ton (VAT-incl) that drives the charge — the
   *  coordinator-editable Freight/Ton, which defaults from the FO rate (and, for read-only/
   *  submitted SOs, is the frozen submitted value). Resolved by the parent. */
  freightPerTon: number | null;
  waivedFreight: string;
  isDiscountedFreight: boolean;
  separateBilling: string;
  includeFreightLine: boolean | null;
  /** FO detail still loading — skeleton the FO-derived stats (86d3a1q88). */
  isLoading?: boolean;
  /** 86d3cwcn9: read-only/submitted frozen pricing snapshot (live FO is null there). When
   *  set, the rebar rollup uses these instead of the FO so the order total keeps the markup.
   *  Keyed by SO grade code (33/40/60/75/80). Omit for editable drafts (live FO path). */
  frozenMarkups?: Record<string, number>;
  frozenTaxRate?: number;
}

export function SalesOrderFreightBlock({
  lineItems,
  firmOffer,
  freightPerTon,
  waivedFreight,
  isDiscountedFreight,
  separateBilling,
  includeFreightLine,
  isLoading,
  frozenMarkups,
  frozenTaxRate,
}: Props) {
  // Pieces × kg per piece — the tonnage the server charges freight on (14ykpqtv3qw).
  const totalWeightKg = totalLineWeightKg(lineItems);
  if (totalWeightKg <= 0) return null;

  if (isLoading && !firmOffer) {
    return (
      <div className="border-t bg-muted/40 px-4 py-4">
        <div className="mb-3 flex items-center gap-2">
          <Truck className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
            Freight &amp; Order Total
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    );
  }

  const rebar = aggregateRebarPricing(lineItems, firmOffer, frozenMarkups, frozenTaxRate);
  const freight = computeSoFreight({
    totalWeightKg,
    // 86d3fteqa: the parent passes the effective editable Freight/Ton rate (defaults from FO;
    // frozen submitted value for read-only SOs), so this block needs no FO/frozen fallback here.
    freightPerTon,
    waived: waivedFreight === '1',
  });
  // When the freight line is omitted (separate billing / incorporated, 86d3addwy) the
  // charge contributes 0 and the order total is rebar-only — same as the submit gate.
  const freightLineIncluded = resolveIncludeFreightLine(separateBilling, includeFreightLine);
  const effectiveFreightCharge = freightLineIncluded ? freight.charge : 0;
  const combined = combinedOrderTotal(rebar.grandTotal, effectiveFreightCharge);

  return (
    <div className="border-t bg-muted/40 px-4 py-4">
      <div className="mb-3 flex items-center gap-2">
        <Truck className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
          Freight &amp; Order Total
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <FreightStat label="Tonnage" value={`${fmtMt(totalWeightKg)} MT`} />
        <FreightStat
          label="Rate / MT"
          value={freight.rate != null ? fmtPhp(freight.rate) : '—'}
        />
        <FreightStat
          label="Freight (VAT-incl.)"
          value={
            !freightLineIncluded
              ? fmtPhp(0)
              : freight.waived
                ? fmtPhp(0)
                : freight.charge != null
                  ? fmtPhp(freight.charge)
                  : '—'
          }
          sub={
            !freightLineIncluded
              ? 'omitted'
              : freight.waived
                ? 'waived'
                : isDiscountedFreight
                  ? 'discounted'
                  : undefined
          }
        />
        <FreightStat label="Order total" value={fmtPhp(combined)} strong />
      </div>
      <p className="mt-2.5 text-xs text-muted-foreground">
        {!freightLineIncluded
          ? 'Freight omitted — no freight line on this order. Order total = rebar only.'
          : 'Order total = rebar grand total (incl. VAT) + freight (VAT-inclusive). Estimate — NetSuite computes the final on submit.'}
        {freightLineIncluded &&
          freight.charge == null &&
          !freight.waived &&
          ' Freight pending an FO rate or actual entry.'}
      </p>
    </div>
  );
}

function FreightStat({
  label,
  value,
  sub,
  strong,
}: {
  label: string;
  value: string;
  sub?: string;
  strong?: boolean;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-0.5 font-mono tabular-nums',
          strong ? 'text-base font-bold text-foreground' : 'text-sm font-semibold',
        )}
      >
        {value}
      </div>
      {sub && <div className="text-xs uppercase tracking-wide text-muted-foreground">{sub}</div>}
    </div>
  );
}
